/**
 * React Hook: useMicrositeAnalytics
 * Provides an easy interface for tracking analytics events in microsite components
 */

import { useCallback } from 'react';
import {
  trackPageView,
  trackPhoneClick,
  trackEmailClick,
  trackWhatsAppClick,
  trackDirectionsClick,
  trackAppointmentBooking,
  trackSocialClick,
  trackProductView,
  trackNavigationClick,
  trackCTAClick,
} from '@/utils/micrositeAnalytics';

export interface UseMicrositeAnalyticsParams {
  subdomainConfigId: string;
  locationId?: string;
  workspaceId: string;
}

export interface UseMicrositeAnalyticsReturn {
  trackPageView: () => void;
  trackPhoneClick: (phoneNumber: string) => void;
  trackEmailClick: (email: string) => void;
  trackWhatsAppClick: () => void;
  trackDirectionsClick: (address: string) => void;
  trackAppointmentBooking: (appointmentData?: Record<string, any>) => void;
  trackSocialClick: (platform: string, url: string) => void;
  trackProductView: (productId: string, productName?: string) => void;
  trackNavigationClick: (destination: string) => void;
  trackCTAClick: (ctaLabel: string, ctaType?: string) => void;
}

/**
 * Hook for tracking microsite analytics events
 *
 * @example
 * ```tsx
 * const analytics = useMicrositeAnalytics({
 *   subdomainConfigId: '123',
 *   locationId: '456',
 *   workspaceId: '789',
 * });
 *
 * // Track page view
 * useEffect(() => {
 *   analytics.trackPageView();
 * }, []);
 *
 * // Track button click
 * <Button onClick={() => analytics.trackPhoneClick('555-1234')}>
 *   Call Now
 * </Button>
 * ```
 */
export function useMicrositeAnalytics(
  params: UseMicrositeAnalyticsParams
): UseMicrositeAnalyticsReturn {
  const { subdomainConfigId, locationId, workspaceId } = params;

  // Memoize tracking functions to prevent unnecessary re-renders
  const trackPageViewHandler = useCallback(() => {
    trackPageView(subdomainConfigId, locationId, workspaceId);
  }, [subdomainConfigId, locationId, workspaceId]);

  const trackPhoneClickHandler = useCallback(
    (phoneNumber: string) => {
      trackPhoneClick(subdomainConfigId, locationId, workspaceId, phoneNumber);
    },
    [subdomainConfigId, locationId, workspaceId]
  );

  const trackEmailClickHandler = useCallback(
    (email: string) => {
      trackEmailClick(subdomainConfigId, locationId, workspaceId, email);
    },
    [subdomainConfigId, locationId, workspaceId]
  );

  const trackWhatsAppClickHandler = useCallback(() => {
    trackWhatsAppClick(subdomainConfigId, locationId, workspaceId);
  }, [subdomainConfigId, locationId, workspaceId]);

  const trackDirectionsClickHandler = useCallback(
    (address: string) => {
      trackDirectionsClick(subdomainConfigId, locationId, workspaceId, address);
    },
    [subdomainConfigId, locationId, workspaceId]
  );

  const trackAppointmentBookingHandler = useCallback(
    (appointmentData?: Record<string, any>) => {
      trackAppointmentBooking(subdomainConfigId, locationId, workspaceId, appointmentData);
    },
    [subdomainConfigId, locationId, workspaceId]
  );

  const trackSocialClickHandler = useCallback(
    (platform: string, url: string) => {
      trackSocialClick(subdomainConfigId, locationId, workspaceId, platform, url);
    },
    [subdomainConfigId, locationId, workspaceId]
  );

  const trackProductViewHandler = useCallback(
    (productId: string, productName?: string) => {
      trackProductView(subdomainConfigId, locationId, workspaceId, productId, productName);
    },
    [subdomainConfigId, locationId, workspaceId]
  );

  const trackNavigationClickHandler = useCallback(
    (destination: string) => {
      trackNavigationClick(subdomainConfigId, locationId, workspaceId, destination);
    },
    [subdomainConfigId, locationId, workspaceId]
  );

  const trackCTAClickHandler = useCallback(
    (ctaLabel: string, ctaType?: string) => {
      trackCTAClick(subdomainConfigId, locationId, workspaceId, ctaLabel, ctaType);
    },
    [subdomainConfigId, locationId, workspaceId]
  );

  return {
    trackPageView: trackPageViewHandler,
    trackPhoneClick: trackPhoneClickHandler,
    trackEmailClick: trackEmailClickHandler,
    trackWhatsAppClick: trackWhatsAppClickHandler,
    trackDirectionsClick: trackDirectionsClickHandler,
    trackAppointmentBooking: trackAppointmentBookingHandler,
    trackSocialClick: trackSocialClickHandler,
    trackProductView: trackProductViewHandler,
    trackNavigationClick: trackNavigationClickHandler,
    trackCTAClick: trackCTAClickHandler,
  };
}
